import { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const PwInput = ({ id, label, value, onChange, show }) => (
  <label className="block">
    <span className="text-xs font-bold tracking-widest uppercase text-gray-500">{label}</span>
    <input data-testid={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} required autoComplete="new-password"
      className="mt-1 w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1] text-sm bg-white" />
  </label>
);

export const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const strong = next.length >= 8 && /[A-Za-z]/.test(next) && /\d/.test(next);
  const matches = next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!strong) return setError("La nueva contraseña debe tener al menos 8 caracteres, con letras y números.");
    if (!matches) return setError("Las contraseñas no coinciden.");
    setSaving(true);
    try {
      await api.changePassword(current, next);
      toast.success("Contraseña actualizada. Usala en tu próximo ingreso.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-gray-200 p-6" data-testid="change-password-form">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-gray-900 inline-flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#0A3977]" /> Cambiar contraseña</h2>
        <button type="button" data-testid="pw-toggle-show" onClick={() => setShow(!show)} className="text-xs font-semibold text-gray-500 inline-flex items-center gap-1 hover:text-gray-900">
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? "Ocultar" : "Mostrar"}
        </button>
      </div>
      <div className="space-y-4">
        <PwInput id="pw-current" label="Contraseña actual" value={current} onChange={setCurrent} show={show} />
        <PwInput id="pw-new" label="Nueva contraseña" value={next} onChange={setNext} show={show} />
        <PwInput id="pw-confirm" label="Repetir nueva contraseña" value={confirm} onChange={setConfirm} show={show} />
        <ul className="text-xs text-gray-500 space-y-0.5">
          <li className={next.length >= 8 ? "text-green-600" : ""}>• Mínimo 8 caracteres</li>
          <li className={/[A-Za-z]/.test(next) && /\d/.test(next) ? "text-green-600" : ""}>• Letras y números</li>
          <li className={confirm && matches ? "text-green-600" : ""}>• Ambas contraseñas iguales</li>
        </ul>
        {error && <p data-testid="pw-error" className="text-sm text-red-600 font-medium">{error}</p>}
        <button data-testid="pw-submit" disabled={saving || !current || !next || !confirm}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A3977] text-white font-bold disabled:opacity-50">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar contraseña
        </button>
      </div>
    </form>
  );
};
